import { Patient, ServiceRequest } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import { ARKON, EXTERNAL_CS, SYSTEMS } from '@consts/systems.consts';
import { getOrderIdSuggestions } from '../utils/fhir-helpers';
import { ConsentStatus } from '@/types/consent.types';
import { formatDate } from '../utils/common-helpers';
import {
  getAllOrderSubStates,
  getSubStateLabel,
} from '../utils/order-substates-helper';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { OrderUtils } from '../utils/order-table.utils';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { ConsentStatusIndicator } from '../components/ConsentStatusIndicator';
import { CONSENT_PROVISION_TYPES } from '@/consts/consent.consts';

export interface OrderSearchParams {
  patientName?: string;
  orderId?: string;
  testType?: string;
  testTypeDisplay?: string;
  status?: string;
  subState?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  consentStatus?: ConsentStatus;
}

export const ORDER_SEARCH_DEFAULTS: OrderSearchParams = {
  patientName: undefined,
  orderId: undefined,
  testType: undefined,
  testTypeDisplay: undefined,
  status: undefined,
  subState: undefined,
  dateFrom: null,
  dateTo: null,
  consentStatus: undefined,
};

export const orderSearchConfig: ResourceSearchConfig<
  OrderSearchParams,
  ServiceRequest
> = {
  resourceType: 'ServiceRequest',
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: FhirSearchParams = {
      category: `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
      _count: itemsPerPage,
      _offset: (page - 1) * itemsPerPage,
      _sort: '-authored',
    };

    if (params.status) {
      query.status = params.status;
    }

    if (params.testType) {
      query.code = params.testType;
    }

    if (params.subState) {
      query._tag = `${ARKON.SD.LAB_ORDER_SUBSTATE}|${params.subState}`;
    }

    if (params.consentStatus) {
      if (params.consentStatus === 'not-answered') {
        query._tag = `${ARKON.SD.CONSENT_STATUS_TAG}|not-answered`;
      } else {
        query._tag = `${ARKON.SD.CONSENT_STATUS_TAG}|${params.consentStatus}`;
      }
    }

    if (params.orderId) {
      query.identifier = params.orderId;
    }

    if (params.patientName) {
      query['subject:Patient.name'] = params.patientName;
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(authored ge ${from} and authored le ${to})`;
    } else if (params.dateFrom) {
      query.authored = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query.authored = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },
  getResourceIdSuggestions: getOrderIdSuggestions,
};

interface OrderFieldOptions {
  t: TFunction<'lims'>;
  searchParams: OrderSearchParams;
  onSearch: (updates: Partial<OrderSearchParams>) => void;
  hidePatientName?: boolean;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
  orderIdQuery: string;
  setOrderIdQuery: (value: string) => void;
  orderIdOptions: string[];
  orderIdLoading: boolean;
}

export function getOrderSearchFields({
  t,
  searchParams,
  onSearch,
  hidePatientName,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
  orderIdQuery,
  setOrderIdQuery,
  orderIdOptions,
  orderIdLoading,
}: OrderFieldOptions): SearchFieldDefinition<OrderSearchParams>[] {
  return [
    !hidePatientName && {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t('orderList.searchPlaceholder', { ns: 'lims' }),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (val: string) => {
        setPatientQuery(val);
        setPatientNameQuery(val);
        if (!val) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (val: string) => onSearch({ patientName: val }),
      onEnter: (val: string) => onSearch({ patientName: val }),
    },
    {
      key: 'orderId',
      type: 'autocomplete',
      placeholder: t('orderList.orderIdPlaceholder', { ns: 'lims' }),
      value: orderIdQuery,
      options: orderIdOptions,
      loading: orderIdLoading,
      onQueryChange: setOrderIdQuery,
      onOptionSubmit: (val: string) => onSearch({ orderId: val }),
      onEnter: (val: string) => onSearch({ orderId: val }),
      onChange: (val: string) => {
        if (!val) {
          onSearch({ orderId: undefined });
        }
      },
    },
    {
      key: 'testType',
      type: 'codeableConcept',
      placeholder: t('orderList.testTypePlaceholder', { ns: 'lims' }),
      binding: ARKON.VS.GENETIC_TESTS_EL,
      defaultValue: searchParams.testType
        ? {
            coding: [
              {
                code: searchParams.testType,
                display: searchParams.testTypeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: any) => {
        const code = value?.coding?.[0]?.code;
        const display = value?.coding?.[0]?.display || value?.text || undefined;
        onSearch({
          testType: code,
          testTypeDisplay: display,
        });
      },
    },
    {
      key: 'subState',
      type: 'select',
      placeholder: t('orderList.table.subStatus', { ns: 'lims' }),
      options: getAllOrderSubStates().map((subState) => ({
        value: subState,
        label: t(getSubStateLabel(subState)),
      })),
      value: searchParams.subState,
      onChange: (value: string | null) =>
        onSearch({ subState: value || undefined }),
    },
    {
      key: 'consentStatus',
      type: 'select',
      placeholder: t('orderList.table.consent', {
        ns: 'lims',
        defaultValue: 'Consent',
      }),
      options: [
        {
          value: 'not-answered',
          label: t('consent.display.notAnswered', { ns: 'lims' }),
        },
        {
          value: CONSENT_PROVISION_TYPES.PERMIT,
          label: t('consent.display.accepted', { ns: 'lims' }),
        },
        {
          value: CONSENT_PROVISION_TYPES.DENY,
          label: t('consent.display.declined', { ns: 'lims' }),
        },
      ],
      value: searchParams.consentStatus,
      onChange: (value: string | null) =>
        onSearch({ consentStatus: (value as ConsentStatus) || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('orderList.fromDatePlaceholder', { ns: 'lims' }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('orderList.toDatePlaceholder', { ns: 'lims' }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<OrderSearchParams>[];
}

interface OrderColumnsOptions {
  t: TFunction<'lims'>;
  patients: Map<string, Patient>;
  hidePatientColumn?: boolean;
}

export function getOrderTableColumns({
  t,
  patients,
  hidePatientColumn,
}: OrderColumnsOptions): ResourceTableColumn<ServiceRequest>[] {
  return [
    {
      key: 'orderId',
      label: t('orderList.table.orderId', { ns: 'lims' }),
      render: (order) => OrderUtils.getOrderId(order),
    },
    {
      key: 'patient',
      label: t('orderList.table.patient', { ns: 'lims' }),
      visible: !hidePatientColumn,
      render: (order) =>
        OrderUtils.getPatientName(
          order,
          patients,
          t('orderList.unknown', 'Unknown')
        ),
    },
    {
      key: 'testType',
      label: t('orderList.table.testType', { ns: 'lims' }),
      render: (order) =>
        OrderUtils.getTestName(
          order,
          t('orderList.unknownTest', 'Unknown Test')
        ),
    },
    {
      key: 'subState',
      label: t('orderList.table.subStatus', { ns: 'lims' }),
      render: (order) => (
        <StatusBadge
          status={OrderUtils.getSubStateStatus(order)}
          label={t(OrderUtils.getSubStateName(order))}
        />
      ),
    },
    {
      key: 'consentStatus',
      label: t('orderList.table.consent', {
        ns: 'lims',
        defaultValue: 'Consent',
      }),
      render: (order) => {
        const consentTag = order.meta?.tag?.find((tag) =>
          tag.system?.includes(ARKON.SD.CONSENT_STATUS_TAG)
        );
        const code = consentTag?.code || 'not-answered';
        return <ConsentStatusIndicator status={code as ConsentStatus} />;
      },
    },
    {
      key: 'date',
      label: t('orderList.table.date', { ns: 'lims' }),
      render: (order) => formatDate(order.authoredOn),
    },
  ];
}
